import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FieldConfig {
  enabled: boolean;
  required?: boolean;
}

export interface SectionConfig {
  enabled: boolean;
  fields: Record<string, FieldConfig>;
}

export interface EntityTypeFieldConfig {
  sections: Record<string, SectionConfig>;
}

/**
 * EntityType model matching the contact_entity_types table.
 */
export interface EntityType {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  displayOrder: number;
  codePrefix: string;
  fieldConfig: EntityTypeFieldConfig;
  createdAt: string;
}

export interface CreateEntityTypePayload {
  name: string;
  description?: string;
  display_order?: number;
  code_prefix?: string;
}

export interface UpdateEntityTypePayload {
  name?: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  code_prefix?: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const entityTypesKeys = {
  all: ['entity-types'] as const,
  list: () => [...entityTypesKeys.all, 'list'] as const,
  fieldConfig: (id: string) => [...entityTypesKeys.all, 'field-config', id] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapEntityType(item: Record<string, unknown>): EntityType {
  return {
    id: item.id as string,
    name: (item.name || '') as string,
    description: (item.description || '') as string,
    isActive: (item.is_active ?? item.isActive ?? true) as boolean,
    displayOrder: (item.display_order ?? item.displayOrder ?? 0) as number,
    codePrefix: (item.code_prefix ?? item.codePrefix ?? '') as string,
    fieldConfig: (item.field_config ??
      item.fieldConfig ?? { sections: {} }) as EntityTypeFieldConfig,
    createdAt: (item.created_at || item.createdAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all entity types for the current tenant.
 */
export function useEntityTypes() {
  return useQuery({
    queryKey: entityTypesKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/entity-types');
      const raw = response.data.data || [];
      return raw.map(mapEntityType);
    },
  });
}

/**
 * Fetch the field configuration for a specific entity type.
 */
export function useEntityTypeFieldConfig(id: string) {
  return useQuery({
    queryKey: entityTypesKeys.fieldConfig(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<EntityTypeFieldConfig>>(
        `/entity-types/${id}/field-config`,
      );
      return response.data.data ?? { sections: {} };
    },
    enabled: !!id,
  });
}

/**
 * Save the field configuration for an entity type.
 */
export function useUpdateEntityTypeFieldConfig(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: EntityTypeFieldConfig) => {
      const response = await apiClient.put<ApiResponse<EntityTypeFieldConfig>>(
        `/entity-types/${id}/field-config`,
        config,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypesKeys.fieldConfig(id) });
      queryClient.invalidateQueries({ queryKey: entityTypesKeys.list() });
    },
  });
}

/**
 * Create a new entity type.
 */
export function useCreateEntityType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateEntityTypePayload) => {
      const response = await apiClient.post<ApiResponse<EntityType>>('/entity-types', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypesKeys.list() });
    },
  });
}

/**
 * Update an existing entity type.
 */
export function useUpdateEntityType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateEntityTypePayload }) => {
      const response = await apiClient.put<ApiResponse<EntityType>>(`/entity-types/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypesKeys.list() });
    },
  });
}

/**
 * Soft-delete (deactivate) an entity type by toggling is_active to false.
 */
export function useDeleteEntityType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/entity-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypesKeys.list() });
    },
  });
}
